// Wire types for the market-maker control plane. These mirror
// contracts/httpapi/v1/mm.go on the Go side. A Go field-tag change
// without a TS counterpart is the first thing to suspect on any
// runtime "undefined is not a function" — keep these in sync.

import type { CurrencyCode } from "./currency.ts";

/**
 * The canonical execution-mode enum. There are exactly two values and
 * deliberately no BOTH/ALL: a preset, a run and an order are each
 * single-mode, and an account runs both channels by starting two runs
 * (SM-525). The console's "All" list tab is a VIEW filter, never a
 * value of this type — which is why it is `TradingMode | ""` at the
 * filter call sites and not a third member here.
 *
 * It lives in the wire-type module (rather than tradingMode.ts, which
 * holds the behaviour) so the wire types need no import cycle back
 * into their own helpers.
 */
export type TradingMode = "CASH" | "GAME";

export const TRADING_MODES: readonly TradingMode[] = ["CASH", "GAME"] as const;

export interface AdminMe {
  user_id: string;
  email: string;
  role: string;
  csrf_token?: string;
  expires_at?: string;
}

/** The acting admin's server-resolved roles and permission codes. */
export interface MyPermissions {
  admin_id: string;
  email: string;
  display_name?: string;
  roles: string[];
  permissions: string[];
}

export interface AdminLoginResponse extends AdminMe {
  display_name?: string;
  roles?: string[];
  permissions?: string[];
}

export interface AdminLoginMFAFactor {
  factor_id: string;
  factor_kind: string;
  display_label?: string;
}

export interface AdminLoginMFAChallenge {
  needs_mfa: true;
  challenge_id: string;
  admin_id: string;
  email: string;
  factors: AdminLoginMFAFactor[];
}

export interface PageInfo {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface MMStrategyParamSpec {
  name: string;
  // "price" (SM-461): a wire price integer at price_precision 4 —
  // operators read/type fractional dollars ($0.00–$1.00), the wire
  // carries the integer (5000 = $0.5000, SM-460 matrix).
  // "money" (SM-470) is a CLIENT-SIDE classification, not a server
  // kind: the catalog still declares these params as "int64". See
  // MONEY_PARAM_NAMES in lib/strategyParams.ts for why, and for the
  // card that moves the declaration server-side.
  kind: "int" | "int64" | "percent" | "bps" | "bool" | "duration_ms" | "ticks" | "string" | "price" | "money";
  label: string;
  description: string;
  default?: number | string | boolean;
  min?: number;
  max?: number;
  required: boolean;
}

export interface MMStrategyDescriptor {
  name: string;
  title: string;
  description: string;
  composite: boolean;
  composes?: string[];
  params: MMStrategyParamSpec[];
}

export interface MMStrategiesResponse {
  strategies: MMStrategyDescriptor[];
}

export interface MMMarketSummary {
  market_id: string;
  event_id?: string;
  event_name?: string;
  name?: string;
  ticker?: string;
  tick_size: number;
  lot_size: number;
  min_amount: number;
  price_min: number;
  price_max: number;
  price_precision: number;
  amount_precision: number;
  trading_starts_at: string;
  trading_ends_at: string;
  updated_at: string;

  /**
   * The execution modes this market is currently eligible for (SM-525).
   * Every OPEN market is CASH-eligible; "GAME" appears only when the
   * market has an effectively-enabled GAME trading channel.
   *
   * Absent/empty reads as ["CASH"] — the pre-SM-525 server shape, where
   * every listed market was a CASH market. Read it through
   * `marketModes()` rather than inline, so the default lives in one
   * place.
   */
  trading_modes?: TradingMode[];
  /** The GAME book id, when this market has one. Auditability only. */
  game_channel_id?: string;
}

export interface MMMarketsResponse {
  markets: MMMarketSummary[];
}

export interface MMStrategyParams {
  levels: number;
  spread_ticks: number;
  step_ticks: number;
  size_per_level: number;
  size_decay_percent: number;
  drift_bps: number;
  jitter_bps: number;
  size_skew_percent: number;
  min_spread_ticks: number;
  max_loss_budget: number;
  initial_yes_probability_bps: number;
  bid_ask_spread_bps: number;
  skew_sensitivity_bps: number;
  inventory_max: number;
  price_floor_bps: number;
}

export interface MMPoolConfig {
  maker_account_mode: "single" | "split";
  bid_maker_count: number;
  ask_maker_count: number;
  taker_count: number;
  funding_minor: number;
  role_name: string;
  email_pattern: string;
  password_pattern: string;
  display_pattern: string;
  maker_user_ids?: string[];
  bid_maker_user_ids?: string[];
  ask_maker_user_ids?: string[];
  taker_user_ids?: string[];
}

export interface MMFairValueConfig {
  init_range_pct: number;
  drift_bps: number;
  reversion_percent: number;
}

export interface MMTakerLoopConfig {
  enabled: boolean;
  interval_ms: number;
  orders_per_tick: number;
  qty_min: number;
  qty_max: number;
  price_offset_ticks: number;
  buy_bias_percent: number;
}

export interface MMBudgetLimits {
  global_max_orders: number;
  global_max_notional: number;
  maker_max_orders: number;
  maker_max_notional: number;
  taker_max_orders: number;
  taker_max_notional: number;
}

export interface MMRiskLimits {
  max_loss_minor: number;
  risk_budget_minor: number;
  initial_funding_minor: number;
}

export interface MMSchedule {
  start_at?: string | null;
  end_at?: string | null;
}

export interface MMMarketSelection {
  mode: "all" | "allowlist";
  allowlist: string[];
}

export interface MMQuoteManagementConfig {
  replenish_on_partial_fill: boolean;
  replenish_threshold_percent: number;
  in_flight_grace_ms: number;
}

export interface MMSellPolicyConfig {
  allow_uncovered_sell: boolean;
  uncovered_fallback: "buy_opposite_outcome";
  partial_inventory_mode: "split";
}

// Body of PATCH /admin/mm/presets/{id}/markets. Surgical edit
// the Instruments page uses to update a preset's selection without
// round-tripping the rest of the RunConfig.
export interface MMUpdateMarketsRequest {
  markets: MMMarketSelection;
}

export interface MMRunConfig {
  strategy: { name: string; params: MMStrategyParams };
  pools: MMPoolConfig;
  fair_value: MMFairValueConfig;
  taker: MMTakerLoopConfig;
  quote_management?: MMQuoteManagementConfig | null;
  sell_policy?: MMSellPolicyConfig | null;
  risk: MMRiskLimits;
  budgets: MMBudgetLimits;
  schedule: MMSchedule;
  markets: MMMarketSelection;
  order_mode: "internal" | "openapi";
  discovery_interval_ms: number;
  refresh_interval_ms: number;
  refresh_jitter_ms: number;
  depth_levels: number;
  min_spread_ticks: number;

  /**
   * The run's trading-channel dimension (SM-450). "CASH" (the default;
   * absent reads as CASH per G3 — every pre-05b snapshot is a CASH run)
   * or "GAME". Named trading_mode, never bare "mode": this console
   * already has order_mode and badge-style modes, and a bare one would
   * be ambiguous at every call site.
   */
  trading_mode?: "CASH" | "GAME";
  /**
   * The explicit authorised opt-in a GAME config must carry before it
   * validates. GAME participation is never implied, defaulted or
   * inherited — a preset duplicated from a GAME preset does not
   * silently keep trading RED.
   */
  game_opt_in?: boolean;
  /** Which channel currency policy the run was authored against; 0 = v1. */
  currency_policy_version?: number;
}

export interface MMPreset {
  /**
   * The preset's single execution mode, already defaulted server-side —
   * always "CASH" or "GAME", never absent on a mode-aware server. It
   * mirrors config.trading_mode and exists so a list row can render its
   * badge without loading the whole config.
   */
  trading_mode?: TradingMode;
  preset_id: string;
  name: string;
  description: string;
  config: MMRunConfig;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  /** @deprecated Alias of auto_start_enabled, derived server-side.
   *  Read auto_start_enabled. */
  is_default: boolean;
  /** Whether the supervisor starts this preset after a service
   *  restart. ANY number of presets may carry it. */
  auto_start_enabled: boolean;
  /** Detail read only. */
  runs?: MMPresetRunSummary;
}

export interface MMPresetsResponse {
  presets: MMPreset[];
  page_info: PageInfo;
}

export type RunStatus =
  | "PENDING"
  | "RUNNING"
  | "PAUSING"
  | "PAUSED"
  | "RESUMING"
  | "STOPPING"
  | "STOPPED"
  | "FAILED";

/**
 * Statuses in which a run still OWNS its accounts, its mode
 * reservation and its concurrent-run slot.
 *
 * PAUSED is in this set: it holds no engine loops, but it holds its
 * desks. Treating it as finished is what would let another run claim
 * the accounts of a bot an operator parked deliberately.
 */
export const ACTIVE_RUN_STATUSES: readonly RunStatus[] = [
  "PENDING",
  "RUNNING",
  "PAUSING",
  "PAUSED",
  "RESUMING",
  "STOPPING",
] as const;

export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = ["STOPPED", "FAILED"] as const;

/** Where a run-state read's rows came from. */
export type MMStateSource = "LIVE" | "SNAPSHOT" | "CHECKPOINT";

/**
 * How complete a state section is.
 *
 * UNAVAILABLE and a COMPLETE section with zero rows are DIFFERENT
 * answers — "we could not read it" versus "we read it and there was
 * nothing". Never collapse them: one means investigate, the other
 * means the desk is empty.
 */
export type MMSectionCompleteness = "COMPLETE" | "PARTIAL" | "UNAVAILABLE" | "NOT_CAPTURED";

/** Source and freshness header on every run-state read. */
export interface MMRunStateMetadata {
  source: MMStateSource;
  captured_at: string;
  completeness: MMSectionCompleteness;
  /** Data known to predate the run's own end (a post-crash checkpoint). */
  stale?: boolean;
  warning?: string;
}

/** One run's place in a recovery chain. Attempt 1 = the original. */
export interface MMRunRecovery {
  attempt: number;
  resumed_from_run_id?: string;
  root_run_id: string;
}

/** Provenance of a run's displayed preset name. */
export type MMPresetNameSource = "CAPTURED" | "RESOLVED" | "FALLBACK";

/**
 * The newest durable restore point.
 *
 * `available: false` WITH a reason is a stable answer, not an error:
 * the console disables checkpoint restart and says why, rather than
 * offering an action that would silently start fresh.
 */
export interface MMRunCheckpointInfo {
  available: boolean;
  version?: number;
  revision?: number;
  stage?: string;
  captured_at?: string;
  reason?: string;
}

/** One captured slice of run state, with its own provenance. */
export interface MMRunSnapshotSection {
  section: MMRunStateSection;
  captured_at: string;
  source?: string;
  completeness: MMSectionCompleteness;
  item_count: number;
  warning?: string;
}

export type MMRunStateSection = "orders" | "positions" | "trades" | "balances";

/**
 * A point-in-time capture's header.
 *
 * An ABSENT snapshot block means "not captured" — which must not be
 * drawn as an empty state view.
 */
export interface MMRunSnapshotInfo {
  revision: number;
  captured_at: string;
  reason?: string;
  complete: boolean;
  sections?: MMRunSnapshotSection[];
}

/**
 * Which lifecycle actions are legal right now, as the SERVER sees it.
 *
 * Rendered from rather than re-derived: a console that inferred
 * "can I resume" from the status string drifts the moment a status is
 * added, and the failure mode is an enabled button that 409s.
 */
export interface MMRunCapabilities {
  can_pause: boolean;
  can_resume: boolean;
  can_stop: boolean;
  can_restart_from_checkpoint: boolean;
  restart_blocked_reason?: string;
  can_start_fresh: boolean;
}

/** One open position owned by a run account. */
export interface MMRunPosition {
  position_id: string;
  run_id: string;
  user_id: string;
  scope: string;
  trading_mode: string;
  trading_channel_id?: string;
  currency: string;
  market_id: string;
  outcome_id?: string;
  outcome_code?: string;
  direction: string;
  quantity: string;
  amount_precision?: number;
  status: string;
}

export interface MMRunPositions {
  run_id: string;
  updated_at: string;
  positions: MMRunPosition[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

/**
 * One (account, currency) execution wallet.
 *
 * `available_minor` absent + `unavailable: true` means the read
 * failed; a present 0 means the wallet is empty. Never `?? 0` these.
 * One row per currency — RED, GREEN and GOLD are separate wallets and
 * a sum corresponds to none of them.
 */
export interface MMRunBalance {
  run_id: string;
  user_id: string;
  scope: string;
  trading_mode: string;
  currency: string;
  available_minor?: number;
  reserved_minor?: number;
  unavailable?: boolean;
}

export interface MMRunBalances {
  run_id: string;
  updated_at: string;
  balances: MMRunBalance[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

/** One preset's boot outcome. */
export type MMAutoStartOutcome = "STARTED" | "RECOVERED" | "SKIPPED" | "FAILED";

export interface MMAutoStartResult {
  preset_id: string;
  preset_name: string;
  /**
   * What the restart DID for this preset — created a run, created a
   * recovery attempt, skipped, or could not dispatch. Immutable for the
   * life of the process (audit). It is NOT the run's health: a run is
   * created PENDING and bootstraps asynchronously, so STARTED here and
   * FAILED in `run` are routinely both true of the same row (SM-547).
   */
  outcome: MMAutoStartOutcome;
  run_id?: string;
  reason?: string;
  /**
   * The linked run's CURRENT lifecycle state, read by the server on
   * every request. Absent when the outcome created no run or the row
   * can no longer be read.
   */
  run?: MMAutoStartRunState;
}

/** Live projection of the run an auto-start row points at (SM-547). */
export interface MMAutoStartRunState {
  run_id: string;
  status: RunStatus;
  is_active: boolean;
  trading_mode?: TradingMode;
  start_at?: string;
  stop_at?: string;
  stop_reason?: string;
  /** The recorded failure, verbatim; `error_code` is its leading code. */
  last_error?: string;
  error_code?: string;
}

/**
 * The market-maker runtime's EFFECTIVE configuration for the switches
 * an operator reasons about before a start (SM-531 / SM-506) — what the
 * process LOADED, not what a ConfigMap says. Shown beside this console's
 * own build-time flags so drift between the two halves is visible.
 */
export interface MMCapabilities {
  game_visibility: boolean;
  game_trading: boolean;
  game_min_reward_capacity_minor: number;
  funding_mode: "admin_credit" | "system_transfer" | string;
  funding_account_code: string;
  funding_auto_top_up_from_parent: boolean;
  /** Per-maker ceiling on one auto top-up; 0 means no ceiling. */
  funding_auto_top_up_max_minor: number;
  funding_allow_legacy_fallback: boolean;
  auto_start: boolean;
  max_concurrent_runs: number;
}

export interface MMAutoStartResultsResponse {
  booted_at?: string;
  results: MMAutoStartResult[];
}

/** Counts of a preset's related runs, for the detail header. */
export interface MMPresetRunSummary {
  total: number;
  active: number;
  failed: number;
  current_run_id?: string;
  latest_terminal_run_id?: string;
  last_started_at?: string;
}

// MMFundingInfo — SM-152 funding requirement/availability summary from
// POST /admin/mm/funding/preview and the run detail's `funding` block.
// mode "system" = MM_FUNDING_MODE=system_transfer (maker wallets draw
// from source_account_code); "fallback" = legacy admin-credit mints,
// where there is no balance to run out of so sufficient is always true
// and the balance fields are omitted.
//
// UNITS: every *_minor field is int64 minor units at the PUBLIC 4dp
// cash scale — the same scale as MMPoolConfig.funding_minor.
// account_count mirrors what bootstrap actually provisions: single
// maker mode shares one account across bid+ask so it counts 1 + takers;
// split mode counts bid + ask + takers. total_required_minor is the
// worst case (all wallets fresh) — already-funded wallets only draw
// their shortfall, so sufficient=false is a pre-flight warning and the
// authoritative check happens at run start.
export interface MMFundingInfo {
  mode: "system" | "fallback";
  source_account_code?: string;
  per_account_funding_minor: number;
  account_count: number;
  total_required_minor: number;
  available_minor?: number;
  sufficient: boolean;
  /**
   * The currency the top-level *_minor fields are denominated in — the
   * run's debit currency, derived from its mode (CASH→GOLD, GAME→RED).
   */
  currency?: CurrencyCode;
  /**
   * Per-currency breakdown (SM-526). A CASH preview has one GOLD entry;
   * a GAME preview has a RED execution entry AND a GREEN reward-capacity
   * entry.
   *
   * NEVER SUM THESE. The three currencies are separate ledgers with no
   * implicit conversion, so a total across them matches no account, and
   * one currency's surplus is never cover for another's shortfall —
   * which is exactly why `sufficient` above is the AND of these, not a
   * comparison of totals.
   */
  requirements?: MMFundingRequirement[];
  /**
   * Per-maker breakdown (SM-506).
   *
   * A run does NOT draw on the shared pool: each named maker debits its
   * own SYSTEM_MM child reserve, and Treasury balances never roll down
   * from parent to child. Reporting only the pool is what let this
   * panel say "Available 101,499.00 / sufficient" for a run that then
   * died on an empty SYSTEM_MM.MM_2.
   *
   * Empty for a fleet of generated makers — they have no reserve yet
   * and are funded from the pool (see `pool_draw_minor`).
   */
  reserves?: MMFundingReserve[];
  /**
   * What the shared pool is actually on the hook for: generated makers,
   * unlinked makers, and every auto top-up this run would trigger.
   *
   * Compare this against `available_minor` — NOT `total_required_minor`,
   * which includes money that comes out of reserves the pool never sees.
   */
  pool_draw_minor?: number;
  /**
   * Whether a short reserve is topped up from the pool automatically.
   * It changes what a shortfall MEANS — a blocker without it, a pool
   * draw with it — so never render a shortfall without reading this.
   */
  auto_top_up?: boolean;
  /**
   * Identifies the RESOLVED plan these rows were priced from (SM-526):
   * mode, currencies, per-account funding, account count and named
   * accounts — configuration only, never balances. Echo it back as
   * `expected_funding_plan` on the start so the server executes exactly
   * the plan whose rows were shown, or refuses (409 MM_000224) if the
   * preset changed in between.
   */
  plan_fingerprint?: string;
}

/** One maker's reserve position for this run. */
export interface MMFundingReserve {
  user_id: string;
  /** The reserve the run will actually debit, resolved server-side. */
  account_code?: string;
  /** No funding link: this maker draws straight on the shared pool. */
  used_fallback?: boolean;
  required_minor: number;
  /**
   * Absent means the balance could NOT be read — materially different
   * from 0 ("read it; it's empty"). An unreadable reserve reports
   * `sufficient: true`; unknown must not render as a shortfall.
   */
  available_minor?: number;
  /** What must be allocated by hand, or auto-pulled. 0 when covered. */
  shortfall_minor: number;
  sufficient: boolean;
  reason?: string;
}

/** One currency's slice of a funding preview. */
export interface MMFundingRequirement {
  currency: CurrencyCode;
  /** "execution" (wallet funding) or "rewards" (the GREEN payout pool). */
  purpose: string;
  source_account_code?: string;
  required_minor: number;
  /**
   * Absent means the balance could NOT be read — materially different
   * from 0 ("read it; it's empty"). An unreadable entry reports
   * `sufficient: true`: unknown must not be rendered as a shortfall.
   */
  available_minor?: number;
  sufficient: boolean;
  /** Why an entry is unmet or unreadable; empty when satisfied. */
  reason?: string;
}

/**
 * A selectable market-maker account.
 *
 * TWO OWNERS, NEVER SUMMED (SM-342): `execution_*` is the executable
 * USER wallet — the only money that can place orders — while
 * `funding_reserve_available_minor` is the linked SYSTEM_MM child, a
 * reserve that becomes tradable only after a transfer. Adding them
 * produces a number that matches no wallet and overstates the desk's
 * buying power.
 *
 * `undefined` is NOT zero on any balance field: absent means the value
 * could not be read, 0 means it was read and is empty. The UI renders
 * those differently, so never `?? 0` these.
 */
export interface MMMakerAccount {
  user_id: string;
  /**
   * The maker's broker account and its servicing broker (SM-786) —
   * always the user's one ACTIVE SYSTEM_BROKER account for a maker.
   * Execution balances are read for this account. Absent while
   * FF_BROKER_ATTRIBUTION is off or broker-service could not be read;
   * absent means "not attributed", never a different account.
   */
  broker_account_id?: string;
  broker_id?: string;
  email: string;
  display_name: string;
  status: string;
  roles: string[];
  /** @deprecated Compatibility alias of execution_available_minor
   *  during the SM-342 rollout. Read execution_available_minor. */
  available_minor?: number;
  execution_available_minor?: number;
  execution_reserved_minor?: number;
  funding_account_code?: string;
  funding_reserve_available_minor?: number;
  funding_link_status?: MMFundingLinkStatus;
  funding_link_error?: string;
  fee_override_id?: string;
  maker_fee_rate?: string;
  taker_fee_rate?: string;
  fee_rate_precision?: number;
  active_run_id?: string;
  active_preset_id?: string;
  /** Human label for the bot occupying this desk — the run's captured
   *  preset name, or the server's deterministic fallback. */
  active_preset_name?: string;
  active_run_status?: RunStatus;
  active_strategy?: string;
  active_trading_mode?: string;
  created_at?: string;
  last_seen_at?: string;
  /** Which wallets the linked reserve actually holds (SM-526), one
   *  entry per currency either mode can require.
   *
   *  ABSENT IS NOT UNREADY. Omitted for an unlinked account, and when
   *  the ledger could not be read — so `undefined` means "not known"
   *  and the account stays selectable. Reading it as unprovisioned
   *  would empty the operator's picker whenever ledger blipped. */
  reserve_currencies?: MMReserveCurrency[];
  /** Whether this account may be SELECTED for each trading mode,
   *  derived server-side by the same predicate run admission
   *  enforces — so the picker cannot offer what a start would refuse. */
  mode_readiness?: MMModeReadiness[];
  /**
   * Every active run holding this account, at most one per trading mode
   * (SM-525). The scalar active_* fields above describe only the FIRST
   * entry, which is exactly the dual-mode case they cannot represent
   * (SM-564). Absent = idle.
   */
  active_runs?: MMAccountActiveRun[];
}

export interface MMAccountActiveRun {
  trading_mode: TradingMode | string;
  run_id: string;
  preset_id?: string;
  preset_name?: string;
  status: RunStatus | string;
  strategy?: string;
}

/** One currency's provisioning state on an account's funding reserve. */
export interface MMReserveCurrency {
  currency: string;
  provisioned: boolean;
  /** What is missing, for the tooltip. Empty when provisioned. */
  reason?: string;
  /**
   * THIS currency's available reserve balance in wire minor units
   * (SM-570), read from the (account_code, currency) wallet — never
   * another currency's figure and never a sum. The flat
   * `funding_reserve_available_minor` on the account stays GOLD's.
   *
   * undefined is NOT zero, and which absence it is depends on
   * `provisioned`: false means the wallet does not exist, true means it
   * exists and could not be read. Never `?? 0` this.
   */
  reserve_available_minor?: number;
}

/**
 * Whether an account can be selected for one trading mode.
 *
 * CASH readiness is NOT evidence of GAME readiness: a GAME run debits
 * RED and pays out GREEN, two wallets a CASH-only reserve has never
 * needed. Gate a GAME picker on the GAME entry.
 */
export interface MMModeReadiness {
  trading_mode: string;
  ready: boolean;
  /** Wallets that must exist first, in funding order. */
  missing_currencies?: string[];
  reason?: string;
}

/**
 * Reserve-currency repair (SM-526): provision the wallets a linked
 * reserve is missing.
 *
 * Creating a wallet moves NO money — a repaired reserve starts at
 * zero — which is what makes the fleet-wide call safe. `dry_run`
 * previews.
 */
export interface MMRepairReserveCurrenciesRequest {
  dry_run?: boolean;
  user_ids?: string[];
  currencies?: string[];
}

export interface MMRepairReserveCurrenciesResponse {
  dry_run: boolean;
  currencies: string[];
  accounts: number;
  summary: Record<string, number>;
  rows: MMReserveCurrencyRow[];
}

export interface MMReserveCurrencyRow {
  user_id?: string;
  account_code: string;
  currency: string;
  state: MMReserveCurrencyState;
  detail?: string;
}

/** PRESENT and PROVISIONED are both fine; MISSING needs an operator;
 *  UNKNOWN means the ledger could not be read and is explicitly NOT a
 *  blocker; SKIPPED is a maker drawing on the shared pool. */
export type MMReserveCurrencyState =
  | "PRESENT"
  | "PROVISIONED"
  | "MISSING"
  | "UNKNOWN"
  | "SKIPPED";

/**
 * State of the mapping between an execution wallet and its reserve.
 * Describes the LINK, not the money: "LINKED" says we know which
 * reserve funds this wallet, nothing about whether it holds anything.
 */
export type MMFundingLinkStatus = "LINKED" | "UNLINKED" | "INVALID" | "UNAVAILABLE";

export interface MMMakerAccountsResponse {
  accounts: MMMakerAccount[];
  page_info: PageInfo;
}

/**
 * Move execution balance from one MM account to another (SM-380).
 *
 * `amount_minor` is wire minor units (see money.ts) and may only draw
 * on the SOURCE'S AVAILABLE balance — reserved funds back open orders
 * and the ledger refuses to move them.
 *
 * `idempotency_key` is minted ONCE per confirmation, not per submit:
 * that is what makes a double-click or a retry after a lost response
 * replay the original transfer instead of moving the money twice.
 */
export interface MMAccountTransferRequest {
  source_user_id: string;
  destination_user_id: string;
  /**
   * The broker accounts the operator was SHOWN (SM-786). Assertions,
   * never routing: the server resolves both accounts itself and
   * refuses 409 when a sent value differs from what it resolved, and
   * 403 BROKER_SCOPE_DENIED when the two sit under different brokers.
   * Sent only when the account rows carried them.
   */
  source_broker_account_id?: string;
  destination_broker_account_id?: string;
  amount_minor: number;
  reason: string;
  idempotency_key: string;
  /**
   * The ONE currency both execution wallets move in (SM-471). Always
   * sent explicitly by this console (SM-568): the server's absent-means-
   * GOLD fold is a compatibility rule, not something a form should lean
   * on when RED and GREEN wallets exist.
   */
  currency: string;
}

/**
 * The committed transfer. Both post-transfer balances are OPTIONAL for
 * the usual reason — absent means "the server could not re-read that
 * wallet", never "the wallet is empty", and never that the transfer
 * failed: by the time these are read it is already committed.
 */
export interface MMAccountTransferResponse {
  transfer_id: string;
  out_transaction_id?: string;
  in_transaction_id?: string;
  source_user_id: string;
  destination_user_id: string;
  /** The broker accounts the move used (SM-786); absent while attribution is off. */
  source_broker_account_id?: string;
  destination_broker_account_id?: string;
  amount_minor: number;
  operation_id: string;
  source_execution_available_minor?: number;
  destination_execution_available_minor?: number;
}

/**
 * Return available execution balance to this MM account's linked
 * SYSTEM_MM reserve. The request intentionally names neither party;
 * the reserve code in the URL resolves the persisted 1:1 link.
 */
export interface MMReturnLinkedAccountRequest {
  amount_minor: number;
  reason: string;
  idempotency_key: string;
  /** Same-currency (execution → reserve) pair. Explicit, see transfer. */
  currency: string;
}

export interface MMReturnLinkedAccountResponse {
  user_id: string;
  funding_account_code: string;
  amount_minor: number;
  operation_id: string;
  execution_available_minor?: number;
  funding_reserve_available_minor?: number;
}

export interface MMFundLinkedAccountRequest {
  amount_minor: number;
  reason: string;
  idempotency_key: string;
  /** Same-currency (reserve → execution) pair. Explicit, see transfer. */
  currency: string;
}

export interface MMFundLinkedAccountResponse {
  user_id: string;
  /** The execution side's broker account (SM-786). */
  broker_account_id?: string;
  funding_account_code: string;
  amount_minor: number;
  operation_id: string;
  execution_available_minor?: number;
  funding_reserve_available_minor?: number;
}

export interface MMMoveReserveToParentRequest {
  amount_minor: number;
  reason: string;
  idempotency_key: string;
  /** The reserve wallet swept to SYSTEM_MM; the parent receives the same currency. */
  currency: string;
}

export interface SystemAccountTransferResponse {
  transfer_id?: string;
  operation_id?: string;
}

export interface MMCreateMakerAccountRequest {
  email?: string;
  password?: string;
  display_name?: string;
  funding_minor?: number;
  account_name?: string;
  code_suffix?: string;
  purpose?: string;
}

export interface MMUpdateMakerAccountFeesRequest {
  maker_fee_rate: string;
  taker_fee_rate: string;
  fee_rate_precision?: number;
  note?: string;
}

export interface MMRun {
  run_id: string;
  preset_id: string;
  config: MMRunConfig;
  status: RunStatus;
  start_requested_at: string;
  start_at?: string | null;
  stop_at?: string | null;
  stop_reason: string;
  created_by: string;
  stopped_by: string;
  last_error: string;
  is_active: boolean;
  /**
   * The preset's name AS IT WAS when this run started, resolved by the
   * server. It does not change when the preset is later renamed or
   * deleted — a history row that adopted a new name would no longer be
   * a record of what happened.
   *
   * Always safe to render: the server substitutes a deterministic
   * fallback ("Deleted preset · …", "Unknown preset · …", "Ad hoc
   * run") rather than sending an empty string, so no client needs its
   * own fallback vocabulary.
   */
  preset_name?: string;
  /** Whether preset_name is a captured fact, a resolution against
   *  today's preset table, or a fallback. */
  preset_name_source?: MMPresetNameSource;
  /**
   * The run's single execution mode, already defaulted server-side.
   * Mirrors config.trading_mode; present so a row renders its badge
   * without digging into the snapshot.
   */
  trading_mode?: TradingMode;
  paused_at?: string | null;
  resumed_at?: string | null;
  recovery: MMRunRecovery;
  capabilities: MMRunCapabilities;
  /** Detail read only. Null/absent means the server did not look —
   *  NOT that no checkpoint exists. */
  checkpoint?: MMRunCheckpointInfo;
  /** Absent means NOT CAPTURED. */
  snapshot?: MMRunSnapshotInfo;
  // Only on the detail read (GET /runs/{id}); omitted on list rows and
  // when the server can't compute it (e.g. ledger unreachable).
  funding?: MMFundingInfo;
}

export interface MMRunsResponse {
  runs: MMRun[];
  page_info: PageInfo;
}

export interface MMRunEvent {
  event_id: string;
  run_id: string;
  occurred_at: string;
  kind: string;
  payload: Record<string, unknown>;
}

export interface MMRunEventsResponse {
  events: MMRunEvent[];
  page_info: PageInfo;
}

export interface MMBudgetUsage {
  scope: "global" | "maker" | "taker";
  orders_placed: number;
  orders_filled: number;
  notional_minor: number;
  risk_loss_minor?: number;
  last_updated_at: string;
}

export interface MMBudgetSnapshot {
  run_id: string;
  limits: MMBudgetLimits;
  usage: MMBudgetUsage[];
  source: "tracker" | "repo";
}

// MMMarketActivity / MMMakerActivity are the per-row shapes of the
// top-N distribution lists inside MMRunStats. Mirrors the wire types
// in contracts/httpapi/v1/mm.go.
export interface MMMarketActivity {
  market_id: string;
  place_attempts: number;
  place_ok: number;
  place_failed: number;
  cancel_attempts: number;
  cancel_ok: number;
  cancel_failed: number;
  last_activity: string;
}

export interface MMMakerActivity {
  user_id: string;
  place_attempts: number;
  place_ok: number;
  place_failed: number;
  cancel_attempts: number;
  cancel_ok: number;
  cancel_failed: number;
  last_activity: string;
}

// MMRunStats is the senior-quant telemetry view of an active run.
// In-memory only on the server — terminated runs return MMRunNotRunning
// rather than this shape (lib/queries.ts surfaces that as null).
export interface MMRunStats {
  run_id: string;
  started_at: string;
  updated_at: string;

  place_attempts: number;
  place_ok: number;
  place_failed: number;
  cancel_attempts: number;
  cancel_ok: number;
  cancel_failed: number;

  buy_orders: number;
  sell_orders: number;
  buy_notional: number;
  sell_notional: number;

  maker_orders: number;
  taker_orders: number;

  errors_by_code?: Record<string, number>;
  top_markets?: MMMarketActivity[];
  top_makers?: MMMakerActivity[];
}

export interface MMRunActiveOrder {
  order_id: string;
  client_order_id?: string;
  /** Correlated run — set on the ACCOUNT-scoped read (SM-564) only. */
  run_id?: string;
  user_id: string;
  scope: "maker" | "taker" | string;
  market_id: string;
  outcome_id?: string;
  outcome_code?: "YES" | "NO" | string;
  side?: "BUY" | "SELL" | string;
  status: string;
  tif?: string;
  price: string;
  quantity?: string;
  filled_quantity?: string;
  remaining_quantity?: string;
  amount_precision?: number;
  created_at: string;
  /** Partition dimensions, on the ROW rather than the response: one
   *  run's accounts can rest orders on more than one book, so a
   *  response-level currency would invite summing across them. */
  trading_mode?: string;
  trading_channel_id?: string;
  currency?: string;
}

export interface MMRunOrderActivity {
  run_id: string;
  updated_at: string;
  active_orders: MMRunActiveOrder[];
  warnings?: string[];
  /** Live read or frozen capture, and how complete. A terminal run
   *  answers through this same shape rather than erroring. */
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

export interface MMRunOrderFill {
  fill_id: number;
  order_id: string;
  trade_id: string;
  /** Correlated run — set on the ACCOUNT-scoped read (SM-564) only. */
  run_id?: string;
  user_id: string;
  scope: "maker" | "taker" | string;
  market_id: string;
  outcome_id?: string;
  outcome_code?: "YES" | "NO" | string;
  side?: "BUY" | "SELL" | string;
  price: string;
  quantity: string;
  amount_precision?: number;
  is_maker: boolean;
  fee_rate: string;
  occurred_at: string;
  /** Partition dimensions — see MMRunActiveOrder. */
  trading_mode?: string;
  trading_channel_id?: string;
  currency?: string;
}

export interface MMRunOrderFills {
  run_id: string;
  updated_at: string;
  fills: MMRunOrderFill[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

// emptyRunConfig builds a sensible blank RunConfig for a new preset
// form. Mirrors the server-side defaults in normalizeConfig().
export function emptyRunConfig(): MMRunConfig {
  return {
    strategy: {
      name: "midpoint_follow+ghost",
      params: {
        levels: 3,
        spread_ticks: 5,
        step_ticks: 5,
        size_per_level: 10,
        size_decay_percent: 80,
        drift_bps: 25,
        jitter_bps: 10,
        size_skew_percent: 40,
        min_spread_ticks: 1,
        max_loss_budget: 10_000,
        initial_yes_probability_bps: 5_000,
        bid_ask_spread_bps: 200,
        skew_sensitivity_bps: 600,
        inventory_max: 20_000,
        price_floor_bps: 10,
      },
    },
    pools: {
      maker_account_mode: "single",
      bid_maker_count: 1,
      ask_maker_count: 1,
      taker_count: 0,
      funding_minor: 100_000_000,
      role_name: "MARKET_MAKER",
      email_pattern: "mm-%d@market-maker.local",
      password_pattern: "mm-pw-%d-do-not-reuse",
      display_pattern: "Market Maker %d",
      maker_user_ids: [],
      bid_maker_user_ids: [],
      ask_maker_user_ids: [],
      taker_user_ids: [],
    },
    fair_value: { init_range_pct: 70, drift_bps: 15, reversion_percent: 5 },
    taker: {
      enabled: false,
      interval_ms: 2000,
      orders_per_tick: 3,
      qty_min: 1,
      qty_max: 5,
      price_offset_ticks: 0,
      buy_bias_percent: 70,
    },
    quote_management: {
      replenish_on_partial_fill: true,
      replenish_threshold_percent: 80,
      in_flight_grace_ms: 15_000,
    },
    sell_policy: {
      allow_uncovered_sell: false,
      uncovered_fallback: "buy_opposite_outcome",
      partial_inventory_mode: "split",
    },
    risk: {
      max_loss_minor: 1_000_000,
      risk_budget_minor: 1_000_000,
      initial_funding_minor: 100_000_000,
    },
    budgets: {
      global_max_orders: 0,
      global_max_notional: 0,
      maker_max_orders: 0,
      maker_max_notional: 0,
      taker_max_orders: 0,
      taker_max_notional: 0,
    },
    schedule: { start_at: null, end_at: null },
    markets: { mode: "all", allowlist: [] },
    order_mode: "internal",
    discovery_interval_ms: 30_000,
    refresh_interval_ms: 5_000,
    refresh_jitter_ms: 1_000,
    depth_levels: 10,
    min_spread_ticks: 1,
  };
}

/**
 * One currency's slice of a linked MM execution wallet (SM-471).
 *
 * NIL IS NOT ZERO — the SM-342 convention, and it matters more here
 * than anywhere: absent means "we could not read that currency's
 * wallet", zero means "we read it and it is empty". An operator
 * deciding whether to fund a desk needs those apart, so nothing in this
 * console may coalesce one into the other.
 */
export interface MMLinkedAccountCurrencyBalance {
  currency: string;
  execution_available_minor?: number;
  execution_reserved_minor?: number;
}

/**
 * `GET /admin/system-accounts/{code}/linked-mm` — which executable
 * account a SYSTEM_MM child funds, and what that wallet family holds.
 *
 * The top-level `execution_*` fields keep their original meaning
 * forever: the GOLD wallet (SM-471 compatibility invariant). `balances`
 * is the additive per-currency view, whose GOLD entry always mirrors
 * them — so a reader that only knows the old fields still reads GOLD
 * correctly, and one that knows both must never add the two together.
 */
export interface MMLinkedAccount {
  funding_account_code: string;
  linked: boolean;
  funding_link_status?: string;
  funding_link_error?: string;
  user_id?: string;
  display_name?: string;
  /** The executable account's broker account (SM-786); execution_* are read for it. */
  broker_account_id?: string;
  execution_available_minor?: number;
  execution_reserved_minor?: number;
  balances?: MMLinkedAccountCurrencyBalance[];
}

// ---- SM-564: one MM account's detail ------------------------------
//
// Every section is its own endpoint so a failing ledger darkens
// Balances alone. Every amount carries the currency of ITS row; no
// section ever returns a cross-currency total.

export interface MMAccountDetail {
  account: MMMakerAccount;
  /** Currencies the account can hold state in — the CASH ∪ GAME wallet set, from the server. */
  currencies: string[];
  updated_at: string;
}

export interface MMAccountBalance {
  currency: string;
  execution_available_minor?: number;
  execution_reserved_minor?: number;
  funding_account_code?: string;
  funding_reserve_available_minor?: number;
  /** Whether the reserve has a wallet in this currency at all; absent = not known. */
  reserve_provisioned?: boolean;
  /** True when the execution read FAILED — distinct from "no wallet yet" (amounts absent, not unavailable). */
  unavailable?: boolean;
}

export interface MMAccountBalances {
  user_id: string;
  /** The broker account every execution figure was read for (SM-786). Absent while attribution is off. */
  broker_account_id?: string;
  updated_at: string;
  balances: MMAccountBalance[];
  warnings?: string[];
  state: MMRunStateMetadata;
}

export interface MMAccountOrderActivity {
  user_id: string;
  updated_at: string;
  active_orders: MMRunActiveOrder[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

export interface MMAccountOrderHistoryItem {
  history_id: number;
  order_id: string;
  run_id?: string;
  user_id: string;
  market_id?: string;
  outcome_id?: string;
  outcome_code?: string;
  side?: string;
  price?: string;
  quantity?: string;
  amount_precision?: number;
  status: string;
  reason?: string;
  trading_mode?: string;
  trading_channel_id?: string;
  currency?: string;
  occurred_at: string;
}

export interface MMAccountOrderHistory {
  user_id: string;
  updated_at: string;
  orders: MMAccountOrderHistoryItem[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

export interface MMAccountOrderFills {
  user_id: string;
  updated_at: string;
  fills: MMRunOrderFill[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

export interface MMAccountPositions {
  user_id: string;
  updated_at: string;
  positions: MMRunPosition[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

export type MMMovementDirection = "CREDIT" | "DEBIT" | "HOLD";

export interface MMAccountBalanceMovement {
  transaction_id: string;
  kind: string;
  direction: MMMovementDirection | string;
  /** Magnitude of the user-facing delta, PUBLIC 4dp minor units. */
  amount_minor: number;
  /** Signed user-facing delta. */
  display_amount_minor: number;
  currency: string;
  operation_id?: string;
  reference_id?: string;
  reason?: string;
  status: string;
  from_account_code?: string;
  to_account_code?: string;
  run_id?: string;
  order_id?: string;
  occurred_at: string;
}

export interface MMAccountBalanceHistory {
  user_id: string;
  /** The broker account the journal was read for (SM-786). */
  broker_account_id?: string;
  currency: string;
  updated_at: string;
  movements: MMAccountBalanceMovement[];
  warnings?: string[];
  state: MMRunStateMetadata;
  page_info: PageInfo;
}

export interface MMAccountRun {
  run_id: string;
  preset_id: string;
  preset_name?: string;
  preset_name_source?: MMPresetNameSource | string;
  status: RunStatus | string;
  trading_mode: TradingMode | string;
  strategy?: string;
  debit_currency: string;
  payout_currency: string;
  /** This account's role in the run: maker, taker, or absent when only the selection names it. */
  scope?: string;
  start_requested_at: string;
  start_at?: string;
  stop_at?: string;
  stop_reason?: string;
  last_error?: string;
  is_active: boolean;
  root_run_id: string;
  attempt: number;
}

export interface MMAccountRuns {
  user_id: string;
  runs: MMAccountRun[];
  page_info: PageInfo;
}
